import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import FormattedCurrency from '@s/FormattedCurrency';
import { removeTicket, changeSectionLevel, setPyosLoading, PYOS_REMOVING_SEATS_REASON, PYOS_UPDATE_OPTION_REASON, PYOS_SELECTING_SEATS_REASON } from '../../../actions/reservedSeating';
import { getSectionPriceLevels, calculateSeatPricing } from '../../../selectors/reserved-seating';
import { createLoadingSelector } from '../../../selectors/helpers';
import Translation from '@s/Translation';
import { getFeeDisplayOption } from '../../../selectors/ui';
import { getFetchedEventPriceLevels, getMainEvent, getSelectedEvent } from '../../../selectors/event';
import { TICKET_FEE_SEPARATE } from '../../../reducers/ui';
import { isDynamicListing } from '../../../selectors/dynamic-listing';
import { setShowBestAvailablePicker, setShowPyosPicker } from '@b/checkout/actions/ui';
import { withFocusTrapContext } from '@b/checkout/contexts/FocusTrapContext';
import { focusTraps } from '@b/checkout/contexts/focusTraps';
import { withRouterCompat } from '@h/withRouterCompat';
import { getXssFilter } from '@showclix-shared-util/xssFilter';
import DetailsToggle from '@s/DetailsToggle';

class SeatingChartCartInventory extends Component {
  xssFilter = getXssFilter();
  
  state = {
    openDetailsIds: new Set(),
  }
  
  componentDidUpdate(prevProps) {
    if (prevProps.pyosLoading !== this.props.pyosLoading || prevProps.reservationLoading !== this.props.reservationLoading) {
      this.props.reactivateFocusTrap(focusTraps.TICKET_PICKER);
    }
  }

  removeTicket = async (ticket, preSaleValid = false) => {
    this.props.setPyosLoading(true, PYOS_REMOVING_SEATS_REASON);
    await this.props.removeTicket(ticket.id, preSaleValid);
    this.props.setPyosLoading(false, null);
  };

  changeTicketOption = async (ticket, sectionLevelId) => {
    this.props.setPyosLoading(true, PYOS_UPDATE_OPTION_REASON);
    await this.props.changeSectionLevel(ticket.id, sectionLevelId);
    this.props.setPyosLoading(false, null);
  };

  disableUi = () => {
    return this.props.pyosLoading || this.props.reservationLoading;
  };

  preSaleValid = () => {
    if (this.props?.coupon?.presaleCode !== "" && this.props?.coupon?.isPresaleValid === true && this.props?.coupon?.isPresaleCodeApplied === true) {
      return true;
    }
    return false
  }

  onShowBestAvailablePicker = (e) => {
    e.preventDefault();
    this.props.navigate('/')
    this.props.setShowBestAvailablePicker(true);
    this.props.setShowPyosPicker(false);
  }

  toggleDetailsOpen = (id) => {
    this.setState(prevState => {
      const openDetailsIds = new Set(prevState.openDetailsIds);
      if (openDetailsIds.has(id)) {
        openDetailsIds.delete(id);
      } else {
        openDetailsIds.add(id);
      }
      return { openDetailsIds };
    });
  }

  render() {
    if (!this.props.tickets.length) {
      if (this.props.pyosLoadingReason === PYOS_SELECTING_SEATS_REASON) {
        return (
                <div className="tickets-table--empty-state">
                    <p>
                        <Translation>{'checkout__reserving_seats'}</Translation>
                    </p>
                    <div className="seated-tickets__reserved-loading-icon">
                        <i className="icon-spinner" aria-hidden="true" />
                    </div>
                </div>
        );
      }
      return (
        <div className="tickets-table--empty-state">
          <p>
              <Translation>{'checkout__select_your_seats_or'}</Translation>{' '}
              <a href={'#'} onClick={this.onShowBestAvailablePicker}><Translation>{'checkout__find_best_available_text'}</Translation></a>
          </p>
        </div>
      );
    }

    return (
      <div className="seated-tickets ticket__item">
        <div className="seated-tickets__header">
          <span>
            <Translation>{'checkout__seat_header'}</Translation>
          </span>
          <span>
            <Translation>{'checkout__option'}</Translation>
          </span>
          <span />
        </div>
        <div
          id="seated-tickets-body"
          className={`seated-tickets__body ${this.disableUi() ? 'seated-tickets__body--loading' : ''}`}
          role="list"
          aria-live='polite'
          aria-atomic='true'
        >
          <h4 className="sr-only"><Translation>{'checkout__selected_seats'}</Translation>{': '}</h4>
          {!!this.disableUi() && (
            <div className="seated-tickets__loading-icon">
              <i className="icon-spinner" aria-hidden="true" />
            </div>
          )}
          {this.props.tickets.map((ticket, index) => {
            const sectionString = ticket?.seat_info?.section ? ticket.seat_info.section : '';
            const rowString = ticket?.seat_info?.row ? ticket.seat_info.row : '';
            const seatString = ticket?.seat_info?.seat ? ticket.seat_info.seat : '';
            const pl_name = ticket.price_level_name ? ticket.price_level_name : '';

            let ticketTitleTranslationVariation = 'checkout__seated_ticket_title_';
            ticketTitleTranslationVariation += sectionString ? 'with_section_' : 'without_section_';
            if(ticket.seat_info?.isTableSeat){
                ticketTitleTranslationVariation += 'with_table_';
            } else {
              ticketTitleTranslationVariation += rowString ? 'with_row_' : 'without_row_';
            }
            ticketTitleTranslationVariation += rowString ? 'with_seat' : 'without_seat';

            const pricing = this.props.seatPricing.find(
              seatPrice => +seatPrice.priceLevelId === +ticket.price_level_id
            );
            const hasPricingOptions = Array.isArray(pricing?.ticketTypes) && pricing.ticketTypes.length > 1;

            const priceLevel = this.props.priceLevels[ticket.price_level_id];

            const { subheading, description } = priceLevel || {};

            return (
              <div className="seated-tickets__body__row" key={index} role="listitem">
                <div className="ticket--seated">
                  <div className="ticket__information">
                    <span className="ticket__name">{pl_name}</span>
                    <span className="ticket__name">
                      <Translation
                        interpolation={{
                          rowNumber: rowString,
                          sectionNumber: sectionString,
                          seatNumber: seatString
                        }}
                      >
                        {ticketTitleTranslationVariation}
                      </Translation>
                    </span>

                    {subheading && (
                      <div className="price-level-subheading">
                        {subheading}
                      </div>
                    )}
                    {description && (
                      <DetailsToggle
                        onClick={this.toggleDetailsOpen}
                        index={ticket.id}
                        aria-expanded={this.state.openDetailsIds.has(ticket.id)}
                        aria-controls={`description_price-level_${ticket.id}`}
                        aria-describedby={`name_price-level_${ticket.id}`}
                      />
                    )}
                    {(this.state.openDetailsIds.has(ticket.id) && description) && (
                      <div className="ticket__description ticket__item">
                        <div
                          id={`description_price-level_${ticket.id}`}
                          className="ticket-description__content"
                          name={`description_${ticket.id}`}
                          dangerouslySetInnerHTML={{
                            __html: this.xssFilter.process(description)
                          }}
                        />
                      </div>
                    )}

                    {+ticket?.buyer_fee?.amount ? (
                      <div className="ticket__fee">
                        <FormattedCurrency>{ticket?.cost?.amount}</FormattedCurrency> +{' '}
                        <FormattedCurrency>{+ticket.buyer_fee.amount + (this.props.discloseFee ? +ticket?.per_ticket_fee?.amount : 0 )}</FormattedCurrency>&nbsp;
                        <Translation>{this.props.feeVerbiage ? 'checkout__fees_label' : 'checkout__service_fee_label'}</Translation>
                      </div>
                    ) : (
                      <div className="ticket__fee">
                        <FormattedCurrency>{ticket?.cost?.amount}</FormattedCurrency>&nbsp;
                        <Translation>{this.props.feeVerbiage ? 'checkout__fees_label' : 'checkout__service_fee_label'}</Translation>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  {hasPricingOptions && (
                    <div className="input__select input__select--clear">
                      <select
                        className="input__control"
                        value={ticket?.seat_info?.section_level_id ?? '0'}
                        onChange={e => {
                          this.changeTicketOption(ticket, e.target.value);
                        }}
                        disabled={!!this.disableUi()}
                      >
                        {pricing.ticketTypes.map((type, index) => {
                          return (
                            <option key={type.pplId} value={type.pplId}>
                              {type.ticketType ?? ''}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                  )}
                </div>

                <div>
                  <button
                    className="button--clear cart-ticket__remove color--link"
                    disabled={!!this.disableUi()}
                    onClick={() => {
                      this.removeTicket(ticket, this.preSaleValid());
                    }}
                  >
                    <i className="icon-close" aria-hidden="true" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }
}

const reservationLoading = createLoadingSelector(['RESERVATION_FETCH', 'RESERVATION_DELETE']);
const mapStateToProps = state => {

  let compareEventId = null;
  if (state.event.fetchedEventId) {
    compareEventId = state.event.fetchedEventId;
  } else if (state.eventSeries.selectedChildEvent) {
    compareEventId = state.eventSeries.selectedChildEvent;
  } else if (isDynamicListing(state)) {
    compareEventId = getSelectedEvent(state)?.event?.event_id;
  }

  let ticketsComparator = compareEventId
    ? (ticket) => ticket.event_id == compareEventId && ticket?.is_seated
    : (ticket) => ticket?.is_seated;

  return {
    discloseFee: getFeeDisplayOption(state) == TICKET_FEE_SEPARATE,
    coupon: state.coupon,
    pyosLoading: state.reservedSeating.pyosLoading,
    reservationLoading: reservationLoading(state),
    tickets: state.tickets.tickets.filter(ticketsComparator),
    seatPricing: calculateSeatPricing(state.priceLevel.levels, getSectionPriceLevels(state),true),
    pyosLoadingReason: state.reservedSeating.pyosLoadingReason,
    feeVerbiage: getMainEvent(state)?.event?.fee_verbiage,
    priceLevels: getFetchedEventPriceLevels(state),
  };
};

const mapDispatchToProps = dispatch => {
  return bindActionCreators(
    {
      removeTicket,
      changeSectionLevel,
      setPyosLoading,
      setShowBestAvailablePicker,
      setShowPyosPicker,
    },
    dispatch
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withFocusTrapContext(withRouterCompat(SeatingChartCartInventory)));
